import { Form, Formik } from "formik";
import React, { FC, ReactElement, useState } from "react";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { AccessProfile } from "../../../models/AccessProfile";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import { assignUnassignAccessProfileGroups } from "../../../services/accessGroupProfileService";
import { createAccessProfile } from "../../../services/accessProfileService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { useDialog } from "../../dialog/DialogContext";
import WizardTwo from "../../wizard/wizardTwo";
import AccessGroupStep from "./AccessGroupStep";
import DetailStep from "./DetailStep";
import SummaryStep from "./SummaryStep";
import Modal from 'react-modal';
import FloorGroupStep from "./FloorGroupStep";

interface AccessProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    setBusy: React.Dispatch<React.SetStateAction<boolean>>;
}

const AccessProfileModal: FC<AccessProfileModalProps> = ({ isOpen, onClose, setBusy }) => {
    const initialValues: AccessProfile = {
        id: 0,
        name: '',
        description: '',
        accessGroups: [],
        floorGroups: [],
        enabled: true
    }
    const { translate } = useLocale();
    const [currentStep, setCurrentStep] = useState(0);
    const dialog = useDialog();
    const [accessProfileName, setAccessProfileName] = useState<string>(translate.byKey(TranslationKeys.pending));
    const [totalAccessGroups, setTotalAccessGroups] = useState<number>(0);
    const [totalFloorGroups, setTotalFloorGroups] = useState<number>(0);

    const steps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.details),
            description: accessProfileName,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.accessGroups),
            description: currentStep > 1 ? translate.byKeyFormatted(TranslationKeys.selectedWithCount, [totalAccessGroups]) : translate.byKey(TranslationKeys.pending),
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.floorGroups),
            description: currentStep > 2 ? translate.byKeyFormatted(TranslationKeys.selectedWithCount, [totalFloorGroups]) : translate.byKey(TranslationKeys.pending),
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        },
    ];

    const onLocalNext = (e: any) => {
        setCurrentStep(currentStep + 1);
    }

    const onLocalBack = (e: any) => {
        setCurrentStep(currentStep - 1);
    }

    const stepContent: ReactElement[] = [
        <DetailStep key={`1`} onNext={onLocalNext} setAccessProfileName={setAccessProfileName}/>,
        <AccessGroupStep key={'2'} onNext={onLocalNext} onBack={onLocalBack} setTotalAccessGroups={setTotalAccessGroups} />,
        <FloorGroupStep key={'3'} onNext={onLocalNext} onBack={onLocalBack} setTotalFloorGroups={setTotalFloorGroups} />,
        <SummaryStep key={`4`} onBack={onLocalBack} />
    ]

    const handleClose = () => {
        setCurrentStep(0);
        setAccessProfileName(translate.byKey(TranslationKeys.pending));
        setTotalAccessGroups(0);
        setTotalFloorGroups(0);
        onClose();
    }

    const onSubmit = (values: any) => {
        setBusy(true);
        createAccessProfile(values).then(async (response: any) => {
            const allAccessGroups = [...(values.accessGroups || []), ...(values.floorGroups || [])];
            await assignUnassignAccessProfileGroups(allAccessGroups, [], response.id);
            handleClose();
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        })
    }

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none w-content">
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={onSubmit}>
            <Form>
                <WizardTwo
                    name={translate.byKey(TranslationKeys.accessProfiles)}
                    step={currentStep}
                    onClose={handleClose}
                    sidebarSteps={steps}
                    stepContent={stepContent} />
            </Form>
        </Formik>
    </Modal>
}

export default AccessProfileModal;